// src/hooks/useSimilarProducts.ts
import { useState, useEffect, useMemo } from 'react';
import { useAllProducts } from '../context/ProductContext';
import { Product } from '../types';
import { getProductCreatedTime } from '../utils/productUtils';

export const useSimilarProducts = (categoryId?: string, currentProductId?: string) => {
  const { allProducts, isLoading: isContextLoading, loadProducts } = useAllProducts();
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Derive the current product to get its tags and price if not passed explicitly
  // But usually we might only have ID. 
  // If we have access to allProducts, we can find the current product there.
  const currentProduct = useMemo(() =>
    allProducts.find(p => p.id === currentProductId),
    [allProducts, currentProductId]);

  useEffect(() => {
    void loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    if (isContextLoading) return;

    setIsLoading(true);

    try {
      // If we don't have products or a reference product, just return empty or random?
      // For now, let's try to match.

      let candidates = allProducts.filter(p => p.id !== currentProductId);

      // Scoring Logic
      const scoredProducts = candidates.map(product => {
        let score = 0;

        // 1. Category Match (+5)
        if (categoryId && product.productCategoryId === categoryId) {
          score += 5;
        }

        // 2. Tag Match (+2 per tag, excluding 'astroji')
        const getFlatTags = (t: any) => {
          if (!t) return [];
          return [
            ...(t.category_tags || []),
            ...(t.purpose_tags || []),
            ...(t.generic_tags || [])
          ];
        };

        if (currentProduct?.tags && product.tags) {
          const currentTags = getFlatTags(currentProduct.tags).filter(t => t !== 'astroji');
          const productTags = getFlatTags(product.tags);
          const intersection = currentTags.filter(t => productTags.includes(t));
          score += (intersection.length * 2);
        }

        // 3. Price Range Match (+3)
        // Let's say within +/- 30% of price
        if (currentProduct?.price) {
          const minPrice = currentProduct.price * 0.7;
          const maxPrice = currentProduct.price * 1.3;
          if (product.price >= minPrice && product.price <= maxPrice) {
            score += 3;
          }
        }

        return { product, score };
      });

      // Sort by score first, then newest product for ties.
      scoredProducts.sort((a, b) =>
        b.score - a.score || getProductCreatedTime(b.product) - getProductCreatedTime(a.product)
      );

      // Take top 8
      const topProducts = scoredProducts.slice(0, 8).map(item => item.product);

      setProducts(topProducts);
    } catch (error) {
      console.error("Error in smart recommendation engine:", error);
    } finally {
      setIsLoading(false);
    }

  }, [allProducts, isContextLoading, categoryId, currentProductId, currentProduct]);

  return { products, isLoading: isLoading || isContextLoading };
};
